import fredokaFont from "@fontsource-variable/fredoka/index.css?url";
import mPlusRounded1cFont from "@fontsource/m-plus-rounded-1c/index.css?url";
import mochiyPopOneFont from "@fontsource/mochiy-pop-one/index.css?url";
import type { QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
	HeadContent,
	Outlet,
	Scripts,
	createRootRouteWithContext,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { PostHogProvider } from "posthog-js/react";
import type * as React from "react";
import { DefaultCatchBoundary } from "~/components/catch-boundary";
import { NotFound } from "~/components/not-found";
import { Toaster } from "~/components/ui/sonner";
import appCss from "~/styles/app.css?url";
import { seo } from "~/utils/seo";
import { m } from "~/i18n/messages";

interface RouteContext {
	queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouteContext>()({
	head: () => ({
		meta: [
			{
				charSet: "utf-8",
			},
			{
				name: "viewport",
				content: "width=device-width, initial-scale=1",
			},
			...seo({
				title: `${m["app.name"]()} | ${m["app.tagline"]()}`,
				description: m["app.seo_description"](),
			}),
		],
		links: [
			{ rel: "stylesheet", href: appCss },
			{ rel: "stylesheet", href: fredokaFont },
			{ rel: "stylesheet", href: mochiyPopOneFont },
			{ rel: "stylesheet", href: mPlusRounded1cFont },
			{
				rel: "apple-touch-icon",
				sizes: "180x180",
				href: "/apple-touch-icon.png",
			},
			{
				rel: "icon",
				type: "image/png",
				sizes: "32x32",
				href: "/favicon-32x32.png",
			},
			{
				rel: "icon",
				type: "image/png",
				sizes: "16x16",
				href: "/favicon-16x16.png",
			},
		],
	}),
	errorComponent: (props) => {
		return (
			<RootDocument>
				<DefaultCatchBoundary {...props} />
			</RootDocument>
		);
	},
	notFoundComponent: () => <NotFound />,
	component: RootComponent,
});

function RootComponent() {
	return (
		<RootDocument>
			{/* only enable posthog in production */}
			{import.meta.env.PROD ? (
				<PostHogProvider
					apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
					options={{
						api_host: "https://us.i.posthog.com",
					}}
				>
					<Outlet />
				</PostHogProvider>
			) : (
				<Outlet />
			)}
		</RootDocument>
	);
}

function RootDocument({ children }: { children: React.ReactNode }) {
	return (
		<html lang="en" className="h-full w-full">
			<head>
				<HeadContent />
				{/* <script
					crossOrigin="anonymous"
					src="//unpkg.com/react-scan/dist/auto.global.js"
				/> */}
			</head>
			<body className="h-full w-full">
				<Toaster richColors theme="light" position="top-right" />
				{children}
				{import.meta.dev && (
					<ReactQueryDevtools buttonPosition="bottom-right" />
				)}
				{import.meta.dev && <TanStackRouterDevtools position="bottom-right" />}
				<Scripts />
			</body>
		</html>
	);
}
